// The learner app's boot and session orchestration, with no UI: who is signed
// in, which content is loaded, what this device remembers. Platform things
// (clock, connectivity, analytics) arrive through SessionDeps.
//
// Load order is forced: session, then content, then store, then engine. The
// progress store needs contentVersion before it can be built, and content
// resolution is async.

import {
	ContentError,
	type ContentSource,
	type LoadedContent,
} from "@aybuben/core/content/source.ts";
import { ONBOARDING_VERSION } from "@aybuben/core/onboarding/version.ts";
import { createStore, type StoreApi } from "zustand/vanilla";

import type { Capture } from "../analytics/events.ts";
import { captureSignOut, type SignOutCause } from "../auth/cause.ts";
import type { SignInMethod } from "../auth/flow.ts";
import { personOf } from "../auth/person.ts";
import { markSecured, securedRecently } from "../auth/secured.ts";
import { AuthError, isGuest, type AuthSource, type AuthUser } from "../auth/source.ts";
import type { DevicePrefs, DeviceStore } from "../device/prefs.ts";
import { audioWindow, orderedUnits, windowKey } from "../offline/audio-sets.ts";
import type { OfflineAudio, SyncRequest } from "../offline/controller.ts";
import type { LoaderConfig } from "../offline/launch.ts";
import { loadWindow, saveWindow } from "../offline/window-memory.ts";
import { clearLocal, forgetUser, rememberedUser, rememberUser } from "../progress/local.ts";
import { createProgressStore, type ProgressStore } from "../progress/store.ts";
import { createEngine, type Engine } from "../sync/engine.ts";
import type { RemoteProgress } from "../sync/remote.ts";

/**
 * Three outcomes, not two. `unknown` is the bootstrap; `absent` is signed out
 * — the ordinary path, never an error screen; `present` is a resolved user,
 * whether resolved by the server or by the remembered session.
 */
export type Phase = { k: "unknown" } | { k: "absent" } | { k: "present"; user: AuthUser };

/** What the session tells analytics. Absent when analytics is not configured: nothing is sent. */
export interface SessionAnalytics {
	capture: Capture;
	identify(id: string, person?: Record<string, unknown>): void;
	setPersonProperties(person: Record<string, unknown>): void;
	reset(): void;
}

/** Platform events the session reacts to. Each returns its unsubscribe. */
export interface SessionLifecycle {
	onOnline(fn: () => void): () => void;
	onOffline(fn: () => void): () => void;
	/** The app came back to the foreground (web: visibilitychange to visible). */
	onForeground(fn: () => void): () => void;
	/** Passed through to the sync engine's `listen` (web: window events). */
	engineListen?: (event: string, fn: () => void) => () => void;
	/** Ask the platform to keep stored recordings (web: navigator.storage.persist). */
	requestPersistence(): void;
}

export interface SessionDeps {
	auth: AuthSource;
	remote: RemoteProgress;
	source: ContentSource;
	deviceStore: DeviceStore;
	offlineAudio: OfflineAudio;
	loaderConfig: LoaderConfig;
	lifecycle: SessionLifecycle;
	analytics?: SessionAnalytics;
	/** Injectable clock, for secureAccount's weekly cache. */
	now?: () => number;
}

export interface SessionState {
	phase: Phase;
	/** Null until loaded: signed-out screens wait for it rather than flash the wrong language. */
	device: DevicePrefs | null;
	content: LoadedContent | null;
	contentError: ContentError | null;
	/** Whose remote log has been pulled in full this session. */
	pulledFor: string | null;
	/** A guest started from onboarding: the app opens their first lesson once. */
	firstLesson: boolean;
	/** The address a leaving guest found taken, prefilled once on sign-in. */
	leftEmail: string;
	/**
	 * The first error an internal subscriber threw, or null. The app rethrows it
	 * at render so its error boundary reports it; see guard.
	 */
	fault: unknown;
}

export interface SessionActions {
	start(): void;
	stop(): void;
	/** The one progress store for this user and content version; the engine reads the same instance. */
	progress(): ProgressStore | null;
	updateDevice(patch: Partial<DevicePrefs>): void;
	retryContent(): void;
	startGuest(): Promise<void>;
	signedIn(user: AuthUser, method: SignInMethod): void;
	linked(user: AuthUser, method: SignInMethod): void;
	leaveGuest(email: string | null): Promise<void>;
	signOut(): Promise<void>;
	deleteAccount(): Promise<void>;
	setSaving(on: boolean): void;
	closeFirstLesson(): void;
}

export type SessionModel = SessionState & SessionActions;
export type Session = StoreApi<SessionModel>;

/**
 * The cause an account failure is recorded under when it does NOT end the
 * session, or null when it does.
 */
function keptCause(e: AuthError): SignOutCause | null {
	switch (e.code) {
		case "network":
			return "accountNetwork";
		case "forbidden":
			return "account403";
		case "rateLimited":
			return "account429";
		case "transient":
			return "account5xx";
		default:
			return null;
	}
}

export function createSession(deps: SessionDeps): Session {
	let teardowns: (() => void)[] = [];
	let started = false;
	// Set while started: reloads the content for the present user.
	let reload: (() => void) | null = null;
	// A guest's save ends their session on purpose; see onUnauthorized.
	let saving = false;
	// Started when content is resolved; stopped on sign-out, a user switch or a
	// new content object. Held here so the sign-out actions reach it.
	let running: { id: string; content: LoadedContent; engine: Engine } | null = null;
	// One store per (user id, content version), rebuilt only when either changes.
	// Deliberately not a default parameter and not an inline
	// `createProgressStore(...)` in a getter: the engine captures its store at
	// construction and subscribes to that instance for its lifetime, so a store
	// built per call sends everything the UI records to an instance the engine
	// never reads, and nothing reaches the server while the session stays open.
	// It would also reset `sync` to "idle" on every call, clearing the
	// learner-visible half of the stale-version guard, because sync status is not
	// part of the persisted payload.
	let held: { id: string; version: string; store: ProgressStore } | null = null;
	const storeFor = (id: string, version: string): ProgressStore => {
		// Keyed on the version as well as the id: a new content release must build a
		// new store so the fold runs under the new content, which is the entire
		// reason content resolution comes before the store.
		if (held?.id !== id || held.version !== version) {
			held = { id, version, store: createProgressStore({ userId: id, content: version }) };
		}
		return held.store;
	};

	const clock = () => (deps.now ?? Date.now)();

	return createStore<SessionModel>()((set, get, api) => {
		// zustand notifies listeners in a forEach: one that throws would skip the
		// rest (account securing among them) and send the error to whoever called
		// set, often a promise nobody awaits. Each internal subscriber is guarded
		// so the others still run, and the first error is kept in state for the
		// app to rethrow where its error boundary reports it.
		let faulted = false;
		const guard =
			<A extends unknown[]>(fn: (...a: A) => void) =>
			(...a: A) => {
				try {
					fn(...a);
				} catch (e) {
					if (faulted) return;
					faulted = true;
					set({ fault: e });
				}
			};

		// The one way a user becomes the present one; sign-in and securing both end here.
		const enter = (u: AuthUser) => {
			rememberUser(u);
			// Consumed: whoever signs in next must not find it prefilled later.
			set({ leftEmail: "", phase: { k: "present", user: u } });
		};
		return {
			phase: { k: "unknown" },
			device: null,
			content: null,
			contentError: null,
			pulledFor: null,
			firstLesson: false,
			leftEmail: "",
			fault: null,

			start() {
				if (started) return;
				started = true;
				// Loaded once; saved on every change (see updateDevice) and once as
				// loaded, so a first launch keeps the language it detected and a
				// corrupt stored value is replaced by what was read from it.
				let live = true;
				teardowns.push(() => {
					live = false;
				});
				void deps.deviceStore.load().then((d) => {
					if (!live) return;
					set({ device: d });
					void deps.deviceStore.save(d);
				});

				const revoke = (cause: SignOutCause, type?: string) => {
					// Only a remembered learner is being signed OUT. Every anonymous launch
					// also lands here with a 401, and counting those would bury the real
					// sign-outs in the cause distribution this event exists to measure.
					if (rememberedUser()) {
						captureSignOut(deps.analytics, cause, type);
						// Identified on an earlier page load; this is a forced sign-out, so
						// clear that persisted analytics identity too.
						deps.analytics?.reset();
					}
					forgetUser();
					set({ phase: { k: "absent" } });
				};

				deps.auth.current().then(
					(u) => {
						if (!live) return;
						if (u) {
							rememberUser(u);
							set({ phase: { k: "present", user: u } });
						}
						// A real 401: this browser is signed out. Drop the remembered id, or
						// the next offline launch would resurrect a session that ended.
						else revoke("account401");
					},
					(e) => {
						if (!live) return;
						// Network, rate limit, server error, forbidden: none of them says the
						// session ended. account.get() offline throws, and a 429 or a 5xx
						// leaves a perfectly valid session behind; destroying the remembered
						// id over one is what turned a server blip into a logged-out learner
						// with unreachable progress. Trusting the remembered id grants no
						// privilege — every remote call still fails until the condition
						// clears, at which point the engine reconciles.
						const failure = e instanceof AuthError ? e : null;
						const kept = failure && keptCause(failure);
						if (kept) {
							const remembered = rememberedUser();
							if (remembered) {
								set({ phase: { k: "present", user: remembered } });
								return;
							}
							// Nobody to fall back on. Recorded under the failure that was hit,
							// so the dashboard can tell whether it is reaching real learners.
							captureSignOut(deps.analytics, kept, failure.appwriteType);
							set({ phase: { k: "absent" } });
							return;
						}
						// An unmapped failure. Treated as the revocation it most likely is;
						// the raw type on the event is what would say otherwise.
						revoke("account401", failure?.appwriteType);
					},
				);

				// Appwrite's $id reaches AuthUser.id, so identity is established once at the
				// session boundary (including a restored session) before loading game data.
				// Email is a person property, never an event property.
				// The proven email already told analytics, so each new one is sent once.
				let identified: { id: string; email: string | null } | null = null;
				const identify = (phase: Phase) => {
					const analytics = deps.analytics;
					if (!analytics) return;
					if (phase.k === "present") {
						const { id } = phase.user;
						const person = personOf(phase.user);
						const was = identified;
						if (was?.id !== id) {
							// Inert for today's sign-out-first flow; keeps a direct account
							// switch from joining two people in one analytics session.
							if (was) analytics.reset();
							analytics.identify(id, person);
						} else if (person && person.email !== was.email) {
							// A guest who saved their progress: the same person, now with a proven address.
							analytics.setPersonProperties(person);
						}
						identified = { id, email: person?.email ?? null };
						return;
					}
					if (phase.k === "absent" && identified) {
						analytics.reset();
						identified = null;
					}
				};
				teardowns.push(
					api.subscribe(
						guard((state, prev) => {
							if (state.phase !== prev.phase) identify(state.phase);
						}),
					),
				);

				// Content resolution phase — runs once the session is present, before store/engine.
				// Each load takes a number; leaving the present phase takes one too, so a
				// load that resolves after the user changed, the session ended or stop()
				// ran is dropped.
				let run = 0;
				let loadedFor: string | null = null;
				const loadContent = (id: string) => {
					const mine = ++run;
					loadedFor = id;
					set({ content: null, contentError: null });
					deps.source.load().then(
						(c) => {
							if (live && mine === run) set({ content: c });
						},
						(e: unknown) => {
							if (!live || mine !== run) return;
							const error =
								e instanceof ContentError ? e : new ContentError("integrity", String(e));
							set({ contentError: error });
							// Handle unauthorized content error: revoked session means the user is signed out.
							// Forget the remembered user and transition to absent phase.
							if (error.kind === "unauthorized") {
								captureSignOut(deps.analytics, "content401");
								forgetUser();
								set({ phase: { k: "absent" } });
							}
						},
					);
				};
				const resolve = (phase: Phase) => {
					if (phase.k === "present") {
						if (phase.user.id !== loadedFor) loadContent(phase.user.id);
						return;
					}
					run++;
					loadedFor = null;
				};
				reload = () => {
					const phase = get().phase;
					if (phase.k === "present") loadContent(phase.user.id);
				};
				teardowns.push(
					api.subscribe(
						guard((state, prev) => {
							if (state.phase !== prev.phase) resolve(state.phase);
						}),
					),
				);
				resolve(get().phase);

				const stopEngine = () => {
					running?.engine.stop();
					running = null;
				};
				const reconcile = () => {
					const { phase, content } = get();
					const id = phase.k === "present" ? phase.user.id : null;
					if (running && running.id === id && running.content === content) return;
					stopEngine();
					if (id === null || !content) return;
					const e = createEngine({
						store: storeFor(id, content.contentVersion),
						remote: deps.remote,
						listen: deps.lifecycle.engineListen,
						onPulled: () => {
							// Dropped once this engine is no longer the running one.
							if (running?.engine === e) set({ pulledFor: id });
						},
						// The server rejected the session mid-use: same outcome as a content 401.
						// Unsynced events stay in the local tail and push after the next sign-in.
						onUnauthorized: () => {
							// Mid-save a guest's session ends on purpose (the code swap deletes
							// it first) and the save settles who this is. Any other 401 is the
							// session really ending, e.g. the email proven on another device.
							const now = get().phase;
							if (now.k === "present" && isGuest(now.user) && saving) return;
							revoke("sync401");
						},
					});
					running = { id, content, engine: e };
					void e.start();
				};
				teardowns.push(stopEngine);
				teardowns.push(
					api.subscribe(
						guard((state, prev) => {
							if (state.phase !== prev.phase || state.content !== prev.content) reconcile();
						}),
					),
				);
				reconcile();

				// Whether progress folds the account's whole history yet. Until it does, a
				// pass deletes nothing and leans on the last known window (audio-sets.ts withHint).
				const isKnown = () => {
					const { phase, pulledFor } = get();
					return phase.k === "present" && pulledFor === phase.user.id;
				};
				let audioRequest: (() => SyncRequest) | null = null;
				let audioOff: (() => void) | null = null;
				let audioFor: { id: string; content: LoadedContent } | null = null;
				const stopAudio = () => {
					audioOff?.();
					audioOff = null;
					audioRequest = null;
					audioFor = null;
				};
				// Recording downloads: a pass at launch, then one whenever the learner's
				// window moves (a unit finished, a remote pull) and when connectivity returns.
				const reconcileAudio = () => {
					const { phase, content } = get();
					const id = phase.k === "present" ? phase.user.id : null;
					if (audioFor && audioFor.id === id && audioFor.content === content) return;
					stopAudio();
					if (id === null || !content) return;
					const progress = storeFor(id, content.contentVersion);
					const lookaheadUnits = deps.loaderConfig.lookaheadUnits;
					const request = (): SyncRequest => {
						const st = progress.getState().state;
						const course = content.course(st.lang);
						const known = isKnown();
						const units = orderedUnits(course);
						if (known) saveWindow(id, audioWindow(units, st, lookaheadUnits), units);
						return {
							course,
							st,
							assets: content.assets,
							lookaheadUnits,
							known,
							hint: known ? null : loadWindow(id, units),
						};
					};
					audioRequest = request;
					audioFor = { id, content };
					deps.lifecycle.requestPersistence();
					const first = request();
					let last = windowKey(first.course, first.st, lookaheadUnits);
					void deps.offlineAudio.request(first);
					const unsubs = [
						progress.subscribe(
							guard((s) => {
								// The store changes on every answer and sync flip; only a moved window needs a pass.
								const k = windowKey(content.course(s.state.lang), s.state, lookaheadUnits);
								if (k === last) return;
								last = k;
								void deps.offlineAudio.request(request());
							}),
						),
						deps.lifecycle.onOnline(() => deps.offlineAudio.setOnline(true)),
						deps.lifecycle.onOffline(() => deps.offlineAudio.setOnline(false)),
						deps.lifecycle.onForeground(() => void deps.offlineAudio.refresh()),
					];
					audioOff = () => {
						for (const u of unsubs) u();
						deps.offlineAudio.stop();
					};
				};
				teardowns.push(stopAudio);
				teardowns.push(
					api.subscribe(
						guard((state, prev) => {
							if (state.phase !== prev.phase || state.content !== prev.content) reconcileAudio();
							// The full history just landed: the window is now right and cleanup is safe.
							if (state.pulledFor !== prev.pulledFor && isKnown() && audioRequest) {
								void deps.offlineAudio.request(audioRequest());
							}
						}),
					),
				);
				reconcileAudio();

				// Whatever someone could still hold after attaching this email to a guest
				// goes once the email is proven (see secureAccountOn in auth/appwrite.ts).
				// Runs on every sign-in and launch of an account with an email, a guest
				// finishing its save included, and may itself prove the account (a Google
				// identity with the same address), which then re-enters as saved.
				let securing: { key: string; cancel: () => void } | null = null;
				const secure = (phase: Phase) => {
					const u = phase.k === "present" && phase.user.email !== "" ? phase.user : null;
					const key = u ? `${u.id}\n${u.email}\n${u.verified}` : null;
					if (key === (securing?.key ?? null)) return;
					securing?.cancel();
					securing = null;
					if (!u || !key) return;
					if (u.verified && securedRecently(u.id, clock())) return;
					let current = true;
					securing = { key, cancel: () => void (current = false) };
					void deps.auth.secureAccount().then((v) => {
						if (v?.verified) markSecured(v.id, clock());
						if (current && live && v && (v.email !== u.email || v.verified !== u.verified))
							enter(v);
					});
				};
				teardowns.push(() => {
					securing?.cancel();
					securing = null;
				});
				teardowns.push(
					api.subscribe(
						guard((state, prev) => {
							if (state.phase !== prev.phase) secure(state.phase);
						}),
					),
				);
				secure(get().phase);
			},
			stop() {
				started = false;
				reload = null;
				held = null;
				const pending = teardowns;
				teardowns = [];
				for (const t of pending) t();
			},
			progress() {
				const { phase, content } = get();
				if (phase.k !== "present" || !content) return null;
				return storeFor(phase.user.id, content.contentVersion);
			},
			updateDevice(patch) {
				const d = get().device;
				if (!d) return;
				const next = { ...d, ...patch };
				set({ device: next });
				void deps.deviceStore.save(next);
			},
			retryContent: () => reload?.(),
			async startGuest() {
				const u = await deps.auth.startGuest();
				// Marked only once the guest exists: an offline tap must leave onboarding
				// in place to retry.
				get().updateDevice({ onboarded: ONBOARDING_VERSION });
				// A session another tab opened is resumed as is (see startGuestOn). A real
				// account is a sign-in: no guest event, no forced lesson, and onboarding
				// waits for its data like any sign-in.
				if (!isGuest(u)) {
					deps.analytics?.capture("session_resumed");
					enter(u);
					return;
				}
				deps.analytics?.capture("guest_started");
				// Not navigation state: /welcome's own redirect to "/" would replace it.
				set({ firstLesson: true });
				enter(u);
			},
			signedIn(u, method) {
				deps.analytics?.capture("signed_in", { method });
				enter(u);
			},
			/** A guest saved their progress. Same id, so the store and engine carry on untouched. */
			linked(u, method) {
				deps.analytics?.capture("account_linked", { method });
				enter(u);
			},
			/**
			 * A guest who wants an existing account instead. Appwrite refuses to open a
			 * session while the guest's is active, and a guest can never sign back in,
			 * so the session and its local cache both go.
			 */
			async leaveGuest(email) {
				const { phase } = get();
				if (phase.k !== "present" || !isGuest(phase.user)) return;
				const guestId = phase.user.id;
				// Only once the sign-out landed: a failed one (offline, a 5xx) leaves the
				// guest in the game, and a stopped engine would not restart for them.
				await deps.auth.signOut();
				running?.engine.stop();
				clearLocal(guestId);
				forgetUser();
				deps.analytics?.capture("guest_left");
				// Session state, not navigation state: the session gate's own redirect
				// would replace it.
				set({ leftEmail: email ?? "", phase: { k: "absent" } });
			},
			async signOut() {
				// Order: flush, sign out, then forget — and do not clear local progress.
				// Flush first so a lesson recorded moments ago is not left waiting behind
				// a session that has already ended. The engine stops when the phase leaves
				// present, after forgetUser; stop() only clears a timer and some
				// listeners, so reordering it changes nothing observable.
				await running?.engine.flush();
				// Only on success. A rejection here is almost always "offline", and
				// clearing the remembered id then would drop a learner at the sign-in
				// wall with their own cached progress sitting unreachable behind it —
				// the failure the bootstrap exists to prevent, reached from the
				// other button. The sheet surfaces the error; the session stays.
				await deps.auth.signOut();
				captureSignOut(deps.analytics, "explicit");
				forgetUser();
				set({ phase: { k: "absent" } });
			},
			async deleteAccount() {
				const { phase } = get();
				await deps.auth.deleteAccount();
				deps.analytics?.capture("account_deleted");
				// Server-side erasure done; now the local cache. clearLocal is called here
				// and by leaveGuest only — sign-out deliberately does not do this.
				if (phase.k === "present") clearLocal(phase.user.id);
				captureSignOut(deps.analytics, "explicit");
				forgetUser();
				set({ phase: { k: "absent" } });
			},
			setSaving(on) {
				saving = on;
			},
			closeFirstLesson() {
				set({ firstLesson: false });
			},
		};
	});
}
